import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  NEXTJS_API_URL,
  clabApplicationAPI,
  rejectionReasonAPI,
} from "../../utils/api";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import Icon from "../../components/AppIcon";
import {
  statusToText,
  getStatusBadgeClass,
  workerStatusHelpers,
  workerStatusToText,
  getWorkerStatusBadgeClass,
  statusHelpers,
} from "../../constants/statusUtils";
import AppLayout from "../../components/ui/AppLayout";
import HeaderBar from "../../components/ui/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import BulkRejectModal from "./BulkRejectModal";
import IndividualRejectModal from "./IndividualRejectModal";
import ArrivalNotificationModal from "./ArrivalNotificationModal";
import Notifier from "../../components/ui/Notifier";
import SpinnerIcon from "../../components/ui/SpinnerIcon";
import ReviewHistory from "../../components/ReviewHistory";
import { useNotifier } from '../../context/NotificationContext';
import { useConfirm } from '../../context/ConfirmContext';

const getUserDisplayName = (user) => {
  if (!user) return "";
  return user.full_name || user.fullName || user.username || "";
};

const sanitizeApiBaseUrl = (rawUrl) => {
  const fallback = "http://localhost:3001/api";
  if (!rawUrl) return fallback;
  const trimmed = rawUrl.endsWith("/") ? rawUrl.slice(0, -1) : rawUrl;
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const EXPORT_API_BASE_URL = sanitizeApiBaseUrl(NEXTJS_API_URL);

export default function SelectionRequestDetailsPage() {
  const notify = useNotifier();
  const confirm = useConfirm();
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [application, setApplication] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingWorker, setRejectingWorker] = useState(null);
  const [rejectForm, setRejectForm] = useState({ reason_id: "", remarks: "" });
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectForm, setBulkRejectForm] = useState({
    reason_id: "",
    remarks: "",
  });
  const [showIndividualRejectModal, setShowIndividualRejectModal] =
    useState(false);
  const [individualRejectForm, setIndividualRejectForm] = useState({
    reason_id: "",
    remarks: "",
  });
  const [notification, setNotification] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const [isUpdating, setIsUpdating] = useState(false); // New state for actions
  const [isExporting, setIsExporting] = useState(false); // Export state
  const [showArrivalModal, setShowArrivalModal] = useState(false); // Arrival notification modal

  const canReleaseWorkers = useMemo(() => {
    if (!user) return false;
    const allowedRoles = new Set([
      "0",
      "1",
      "admin",
      "clab",
      "clab_user",
      "master_admin",
      "master admin",
      "masteradmin",
    ]);
    const rolesToCheck = [user.role_name, user.roleName, user.role].filter(
      (role) => role !== undefined && role !== null
    );
    return rolesToCheck.some((role) =>
      allowedRoles.has(String(role).toLowerCase())
    );
  }, [user]);

  const isApplicationRejected = statusHelpers.isRejected(application?.status);

  const quotaProgress = useMemo(() => {
    const qp = application?.quota_progress || {};
    const total =
      qp.total ??
      application?.quota ??
      application?.fcl_count ??
      workers.length;
    const submitted = qp.submitted ?? workers.length;
    const approved =
      qp.approved ??
      workers.filter((w) => workerStatusHelpers.isApproved(w.status)).length;
    const rejected =
      qp.rejected ??
      workers.filter((w) => workerStatusHelpers.isRejected(w.status)).length;
    const pending =
      qp.pending ??
      workers.filter((w) => workerStatusHelpers.isPending(w.status)).length;
    const remaining = qp.remaining ?? Math.max(total - submitted, 0);
    const quotaRef =
      application?.quota_reference_no || application?.vdr_id || null;
    return {
      total,
      submitted,
      approved,
      rejected,
      pending,
      remaining,
      quotaRef,
    };
  }, [
    application?.fcl_count,
    application?.quota,
    application?.quota_progress,
    application?.quota_reference_no,
    application?.vdr_id,
    workers,
  ]);

  useEffect(() => {
    fetchApplication();
    fetchRejectionReasons();
  }, [applicationId]);

  const fetchApplication = async () => {
    setLoading(true);
    try {
      const result = await clabApplicationAPI.getApplicationDetails(
        applicationId
      );
      const applicationData = result?.application || null;
      const workerData = result?.workers || [];

      setApplication(applicationData);
      setWorkers(workerData);
      setSelectedWorkerIds([]);
      setError(null);
    } catch (err) {
      console.error("Failed to load application details:", err);
      setError("Failed to load application details");
      setApplication(null);
      setWorkers([]);
      setNotification({
        show: true,
        type: "error",
        message: "Failed to load application details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectionReasons = async () => {
    try {
      const response = await rejectionReasonAPI.getActiveReasons();
      let reasons = [];
      if (Array.isArray(response)) {
        reasons = response;
      } else if (Array.isArray(response?.data?.data?.data)) {
        reasons = response.data.data.data;
      } else if (Array.isArray(response?.data?.data)) {
        reasons = response.data.data;
      } else if (Array.isArray(response?.data)) {
        reasons = response.data;
      }
      setRejectionReasons(reasons);
    } catch (error) {
      console.error("Failed to fetch rejection reasons:", error);
      setRejectionReasons([]);
    }
  };

  // Bulk selection logic
  const handleSelectWorker = (workerId) => {
    setSelectedWorkerIds((prev) =>
      prev.includes(workerId)
        ? prev.filter((id) => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleSelectAll = () => {
    const allIds = workers.map((w) => w.id);
    if (selectedWorkerIds.length === allIds.length) {
      setSelectedWorkerIds([]);
    } else {
      setSelectedWorkerIds(allIds);
    }
  };

  // Bulk Approve/Reject
  const handleBulkApprove = async () => {
    if (selectedWorkerIds.length === 0) return;
    setProcessing(true);
    try {
      await Promise.all(
        selectedWorkerIds.map((workerId) =>
          clabApplicationAPI.approveApplicationWorker(applicationId, workerId)
        )
      );
      fetchApplication();
      setSelectedWorkerIds([]);
      setNotification({
        show: true,
        type: "success",
        message: `${selectedWorkerIds.length} worker(s) approved successfully.`,
      });
    } catch (err) {
      console.error("Bulk approve failed:", err);
      setError("Bulk approve failed");
      setNotification({
        show: true,
        type: "error",
        message: "Failed to approve selected workers.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedWorkerIds.length === 0) return;
    // For bulk reject, we'll need to implement a bulk rejection modal
    // For now, let's show an alert
    notify.warning("Please select individual workers to reject with specific reasons");
  };

  // Individual Approve/Reject
  const handleApprove = async (workerId) => {
    setProcessing(true);
    try {
      await clabApplicationAPI.approveApplicationWorker(
        applicationId,
        workerId
      );
      fetchApplication();
      setNotification({
        show: true,
        type: "success",
        message: "Worker approved successfully.",
      });
    } catch (err) {
      console.error("Approve failed:", err);
      setError("Approve failed");
      setNotification({
        show: true,
        type: "error",
        message: "Failed to approve worker.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (worker) => {
    setRejectingWorker(worker);
    setShowRejectModal(true);
  };

  // Release worker back to pool/unselected list
  const handleReleaseWorker = async (worker, mode = "return") => {
    const workerName = worker.biodata?.full_name || "this worker";
    const isRelease = mode === "release";

    const confirmed = await confirm({
      title: isRelease ? `Release ${workerName}?` : `Return ${workerName} to the pool?`,
      message: isRelease
        ? "The worker is detached from the employer and moved back to the unselected workers list."
        : "The worker becomes available for other employers to select.",
      confirmLabel: isRelease ? "Release worker" : "Return to pool",
      tone: "danger",
    });
    if (!confirmed) return;

    setProcessing(true);
    try {
      await clabApplicationAPI.returnWorkerToPool(worker.id);
      fetchApplication();
      setNotification({
        show: true,
        type: "success",
        message: isRelease
          ? `${workerName} has been released and can now be selected by other employers.`
          : `${workerName} has been returned to the approval pool successfully.`,
      });
    } catch (err) {
      console.error("Worker release failed:", err);
      setNotification({
        show: true,
        type: "error",
        message: isRelease
          ? "Failed to release worker. Please try again."
          : "Failed to return worker to the pool. Please try again.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectForm.reason_id) {
      setError("Please select a rejection reason");
      return;
    }

    setProcessing(true);
    try {
      await clabApplicationAPI.rejectApplicationWorkerNew(
        applicationId,
        rejectingWorker.id,
        {
          reason_id: rejectForm.reason_id,
          remarks: rejectForm.remarks,
        }
      );
      fetchApplication();
      setShowRejectModal(false);
      setRejectingWorker(null);
      setRejectForm({ reason_id: "", remarks: "" });
      setNotification({
        show: true,
        type: "success",
        message: "Worker rejected successfully.",
      });
    } catch (err) {
      console.error("Reject failed:", err);
      setError("Reject failed");
      setNotification({
        show: true,
        type: "error",
        message: "Failed to reject worker.",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Totals using status helpers
  const approvedCount = workers.filter((w) =>
    workerStatusHelpers.isApproved(w.status)
  ).length;
  const pendingCount = workers.filter((w) =>
    workerStatusHelpers.isPending(w.status)
  ).length;
  const rejectedCount = workers.filter((w) =>
    workerStatusHelpers.isRejected(w.status)
  ).length;

  const totalApproved =
    workers.length > 0
      ? approvedCount
      : application?.workers_summary?.approved ?? 0;
  const totalPending =
    workers.length > 0
      ? pendingCount
      : application?.workers_summary?.pending ?? 0;
  const totalRejected =
    workers.length > 0
      ? rejectedCount
      : application?.workers_summary?.rejected ?? 0;

  const handleBulkRejectModal = () => {
    if (selectedWorkerIds.length === 0) {
      setNotification({
        show: true,
        type: "error",
        message: "Please select at least one worker to reject.",
      });
      return;
    }
    setShowBulkRejectModal(true);
  };

  const closeBulkRejectModal = () => {
    setShowBulkRejectModal(false);
    setBulkRejectForm({ reason_id: "", remarks: "" });
  };

  const handleBulkRejectSubmit = async () => {
    if (!bulkRejectForm.reason_id) {
      setNotification({
        show: true,
        type: "error",
        message: "Please select a rejection reason",
      });
      return;
    }
    setProcessing(true);
    try {
      await Promise.all(
        selectedWorkerIds.map((workerId) =>
          clabApplicationAPI.rejectApplicationWorkerNew(
            applicationId,
            workerId,
            {
              reason_id: Number(bulkRejectForm.reason_id),
              remarks: bulkRejectForm.remarks,
            }
          )
        )
      );
      setNotification({
        show: true,
        type: "success",
        message: "Selected workers rejected successfully",
      });
      closeBulkRejectModal();
      setSelectedWorkerIds([]);
      fetchApplication();
    } catch (error) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to reject selected workers",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleIndividualReject = async (worker) => {
    setRejectingWorker(worker);
    setShowIndividualRejectModal(true);
    setIndividualRejectForm({ reason_id: "", remarks: "" });
  };

  const closeIndividualRejectModal = () => {
    setShowIndividualRejectModal(false);
    setRejectingWorker(null);
    setIndividualRejectForm({ reason_id: "", remarks: "" });
  };

  const handleIndividualRejectSubmit = async () => {
    if (!individualRejectForm.reason_id) {
      setNotification({
        show: true,
        type: "error",
        message: "Please select a rejection reason",
      });
      return;
    }
    setProcessing(true);
    try {
      await clabApplicationAPI.rejectApplicationWorkerNew(
        applicationId,
        rejectingWorker.id,
        {
          reason_id: Number(individualRejectForm.reason_id),
          remarks: individualRejectForm.remarks,
        }
      );
      setNotification({
        show: true,
        type: "success",
        message: "Worker rejected successfully",
      });
      closeIndividualRejectModal();
      fetchApplication();
    } catch (error) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to reject worker",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedWorkerIds.length === 0) {
      setNotification({
        show: true,
        type: "warning",
        message: "Please select at least one worker.",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        application_id: applicationId,
        worker_ids: selectedWorkerIds,
        status: action,
      };
      await clabApplicationAPI.updateWorkerStatus(payload);
      setNotification({
        show: true,
        type: "success",
        message: `Successfully ${action.toLowerCase()}ed ${
          selectedWorkerIds.length
        } worker(s).`,
      });
      fetchApplication();
      setSelectedWorkerIds([]);
    } catch (err) {
      setNotification({
        show: true,
        type: "error",
        message:
          err.response?.data?.message ||
          `Failed to ${action.toLowerCase()} workers.`,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApproveAll = async () => {
    setIsUpdating(true);
    try {
      await clabApplicationAPI.approveAllWorkers(applicationId);
      setNotification({
        show: true,
        type: "success",
        message: "All workers in the application have been approved.",
      });
      fetchApplication();
    } catch (err) {
      setNotification({
        show: true,
        type: "error",
        message:
          err.response?.data?.message || "Failed to approve all workers.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExportWorkers = async () => {
    if (application?.status !== "approved") {
      setNotification({
        show: true,
        type: "warning",
        message: "Application must be approved before exporting workers.",
      });
      return;
    }

    if (totalApproved === 0) {
      setNotification({
        show: true,
        type: "warning",
        message: "No approved workers to export.",
      });
      return;
    }

    const token =
      localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) {
      setNotification({
        show: true,
        type: "error",
        message: "Authentication required. Please log in again to export.",
      });
      return;
    }

    setIsExporting(true);
    try {
      const response = await fetch(
        `${EXPORT_API_BASE_URL}/clab/applications/${applicationId}/export-workers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        let errorMessage = "Export failed";
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            errorMessage = errorBody.error;
          }
        } catch (jsonError) {
          // Ignore JSON parsing errors and use default error message
        }
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `Application_${applicationId}_Workers.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        show: true,
        type: "success",
        message: `Successfully exported ${totalApproved} approved worker(s) for visa calling.`,
      });
    } catch (err) {
      console.error("Export error:", err);
      setNotification({
        show: true,
        type: "error",
        message: "Failed to export workers. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AppLayout
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
    >
      <HeaderBar
        userRole={user?.role || "clab_user"}
        userName={user?.full_name || user?.fullName || user?.username}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      <div className="space-y-6 max-w-full px-6 pt-20">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate("/clab-selection-requests")}
                  className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-200 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 hover:bg-white/20"
                >
                  <Icon name="ArrowLeft" size={20} />
                  <span className="font-medium">Back</span>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Application Details
                  </h1>
                  <p className="text-blue-100">
                    Review and manage individual worker approvals
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-white/80 text-sm">Status</div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                      application?.status
                    )}`}
                  >
                    {statusToText(application?.status)}
                  </div>
                </div>
                <div className="hidden md:block text-right">
                  <div className="text-white/80 text-sm">VDR Type</div>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-white">
                    <Icon name="Layers" size={16} className="mr-2" />
                    {application?.vdr_type || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <BreadcrumbNavigation
          items={[
            { label: "Dashboard", href: "/clab-dashboard" },
            { label: "Selection Requests", href: "/clab-selection-requests" },
            {
              label: "Application Details",
              href: `/clab-selection-requests/${application?.id}`,
            },
          ]}
        />

        {/* Application Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Application Information
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Icon name="FileText" size={20} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Application No.</div>
                  <div className="font-semibold text-gray-900">
                    {application?.reference_number || `APP-${application?.id}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Icon name="Building" size={20} className="text-green-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Company</div>
                  <div className="font-semibold text-gray-900">
                    {application?.employer?.company_name || "N/A"}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Icon name="Users" size={20} className="text-purple-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Quota</div>
                  <div className="font-semibold text-gray-900">
                    {quotaProgress.total} workers
                  </div>
                  {quotaProgress.quotaRef && (
                    <div className="text-xs text-gray-500">
                      Ref: {quotaProgress.quotaRef}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <Icon name="Layers" size={20} className="text-indigo-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">VDR Type</div>
                  <div className="font-semibold text-gray-900">
                    {application?.vdr_type || "—"}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <Icon name="Calendar" size={20} className="text-orange-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Submission Date</div>
                  <div className="font-semibold text-gray-900">
                    {application?.created_at
                      ? new Date(application.created_at).toLocaleDateString(
                          "en-MY",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          }
                        )
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quota Progress */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Quota Progress
            </h2>
            <div className="text-sm text-gray-500">
              Submitted {quotaProgress.submitted} / {quotaProgress.total}
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                <div className="text-sm text-blue-700">Total Approved</div>
                <div className="text-2xl font-bold text-blue-900">
                  {quotaProgress.total}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                <div className="text-sm text-green-700">Submitted</div>
                <div className="text-2xl font-bold text-green-900">
                  {quotaProgress.submitted}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
                <div className="text-sm text-purple-700">Approved</div>
                <div className="text-2xl font-bold text-purple-900">
                  {quotaProgress.approved}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                <div className="text-sm text-amber-700">Remaining</div>
                <div className="text-2xl font-bold text-amber-900">
                  {quotaProgress.remaining}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{
                    width: `${Math.min(
                      100,
                      (quotaProgress.submitted / (quotaProgress.total || 1)) *
                        100
                    )}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                <span>Submitted</span>
                <span>
                  {quotaProgress.submitted} / {quotaProgress.total}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-green-600 text-sm font-medium">
                  Approved
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {totalApproved}
                </div>
              </div>
              <div className="bg-green-200 p-3 rounded-full">
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-yellow-600 text-sm font-medium">
                  Pending
                </div>
                <div className="text-2xl font-bold text-yellow-700">
                  {totalPending}
                </div>
              </div>
              <div className="bg-yellow-200 p-3 rounded-full">
                <Icon name="Clock" size={24} className="text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-red-600 text-sm font-medium">Rejected</div>
                <div className="text-2xl font-bold text-red-700">
                  {totalRejected}
                </div>
              </div>
              <div className="bg-red-200 p-3 rounded-full">
                <Icon name="XCircle" size={24} className="text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Actions
              </h3>
              <div className="text-sm text-gray-500">
                Select workers for bulk actions
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 hover:scale-105 hover:shadow-lg"
              >
                <Icon name="CheckSquare" size={18} />
                <span className="font-medium">
                  {selectedWorkerIds.length === workers.length
                    ? "Deselect All"
                    : "Select All"}
                </span>
              </button>
              <button
                onClick={handleBulkApprove}
                disabled={selectedWorkerIds.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 hover:shadow-lg"
              >
                <Icon name="CheckCircle" size={18} />
                <span className="font-medium">Approve Selected</span>
              </button>
              <button
                onClick={handleBulkRejectModal}
                disabled={selectedWorkerIds.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 hover:shadow-lg"
              >
                <Icon name="XCircle" size={18} />
                <span className="font-medium">Reject Selected</span>
              </button>
              <button
                onClick={handleApproveAll}
                disabled={isUpdating}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 hover:shadow-lg"
              >
                <Icon name="CheckCircle" size={18} />
                <span className="font-medium">Approve All</span>
              </button>
              {application?.status === "approved" && totalApproved > 0 && (
                <>
                  <button
                    onClick={handleExportWorkers}
                    disabled={isExporting}
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  >
                    {isExporting ? (
                      <>
                        <Icon
                          name="Loader"
                          size={18}
                          className="animate-spin"
                        />
                        <span className="font-medium">Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Download" size={18} />
                        <span className="font-medium">
                          Export for Visa Calling
                        </span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowArrivalModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  >
                    <Icon name="Mail" size={18} />
                    <span className="font-medium">Arrival Notification</span>
                  </button>
                </>
              )}
              <div className="text-sm text-gray-500">
                {selectedWorkerIds.length} worker(s) selected
              </div>
            </div>
          </div>
        </div>

        {/* Workers List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Worker List</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Worker No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nationality
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medical Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entry Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entry Remarks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date H/O Notify
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workers.map((worker, index) => {
                  const normalizedWorkerStatus = String(
                    worker.status || ""
                  ).toLowerCase();
                  return (
                    <tr key={worker.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkerIds.includes(worker.id)}
                            onChange={() => handleSelectWorker(worker.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {index + 1}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.biodata?.full_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.biodata?.passport_no || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.biodata?.nationality || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.biodata?.medical_expiry_date
                          ? new Date(
                              worker.biodata.medical_expiry_date
                            ).toLocaleDateString("en-MY", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.entry_date
                          ? new Date(worker.entry_date).toLocaleDateString(
                              "en-MY",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "-"}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-900 max-w-[150px] truncate"
                        title={worker.entry_remarks || ""}
                      >
                        {worker.entry_remarks || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.email_sent_at
                          ? new Date(worker.email_sent_at).toLocaleDateString(
                              "en-MY",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWorkerStatusBadgeClass(
                            worker.status
                          )}`}
                        >
                          {workerStatusToText(worker.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {workerStatusHelpers.isApproved(worker.status) &&
                            worker.approvedByUser && (
                              <div className="flex items-center space-x-2 text-sm">
                                <Icon
                                  name="CheckCircle"
                                  size={14}
                                  className="text-green-500"
                                />
                                <span className="font-medium text-gray-900">
                                  {getUserDisplayName(worker.approvedByUser)}
                                </span>
                              </div>
                            )}
                          {workerStatusHelpers.isRejected(worker.status) &&
                            worker.rejectedByUser && (
                              <div className="flex items-center space-x-2 text-sm">
                                <Icon
                                  name="XCircle"
                                  size={14}
                                  className="text-red-500"
                                />
                                <span className="font-medium text-gray-900">
                                  {getUserDisplayName(worker.rejectedByUser)}
                                </span>
                              </div>
                            )}
                          {workerStatusHelpers.isApproved(worker.status) &&
                            worker.approved_at && (
                              <div className="text-xs text-gray-500">
                                {new Date(
                                  worker.approved_at
                                ).toLocaleDateString("en-MY", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </div>
                            )}
                          {workerStatusHelpers.isRejected(worker.status) &&
                            worker.rejected_at && (
                              <div className="text-xs text-gray-500">
                                {new Date(
                                  worker.rejected_at
                                ).toLocaleDateString("en-MY", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </div>
                            )}
                          {workerStatusHelpers.isPending(worker.status) && (
                            <span className="text-xs text-gray-400">
                              Pending review
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {workerStatusHelpers.isPending(worker.status) && (
                          <>
                            <button
                              onClick={() => handleApprove(worker.id)}
                              className="text-green-600 hover:text-green-900 transition-colors duration-150"
                              title="Approve Worker"
                            >
                              <Icon name="CheckCircle" size={18} />
                            </button>
                            <button
                              onClick={() => handleIndividualReject(worker)}
                              className="text-red-600 hover:text-red-900 transition-colors duration-150"
                              title="Reject Worker"
                            >
                              <Icon name="XCircle" size={18} />
                            </button>
                          </>
                        )}
                        {workerStatusHelpers.isApproved(worker.status) && (
                          <button
                            onClick={() =>
                              handleReleaseWorker(worker, "return")
                            }
                            disabled={processing}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Return to Approval Pool"
                          >
                            <Icon name="RotateCcw" size={18} />
                          </button>
                        )}
                        {isApplicationRejected &&
                          canReleaseWorkers &&
                          ["rejected", "2"].includes(
                            normalizedWorkerStatus
                          ) && (
                            <button
                              onClick={() =>
                                handleReleaseWorker(worker, "release")
                              }
                              disabled={processing}
                              className="text-indigo-600 hover:text-indigo-900 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Release Worker"
                            >
                              <Icon name="Unlock" size={18} />
                            </button>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Review History */}
        <ReviewHistory application={application} workers={workers} />

        {/* Bulk Reject Modal */}
        <BulkRejectModal
          open={showBulkRejectModal}
          onClose={closeBulkRejectModal}
          onSubmit={handleBulkRejectSubmit}
          form={bulkRejectForm}
          onFormChange={setBulkRejectForm}
          rejectionReasons={rejectionReasons}
          processing={processing}
        />

        {/* Individual Reject Modal */}
        <IndividualRejectModal
          open={showIndividualRejectModal}
          onClose={closeIndividualRejectModal}
          onSubmit={handleIndividualRejectSubmit}
          form={individualRejectForm}
          onFormChange={setIndividualRejectForm}
          rejectionReasons={rejectionReasons}
          processing={processing}
          worker={rejectingWorker}
        />

        {/* Arrival Notification Modal */}
        <ArrivalNotificationModal
          isOpen={showArrivalModal}
          onClose={() => setShowArrivalModal(false)}
          application={application}
          workers={workers}
          onSuccess={(data, action) => {
            setShowArrivalModal(false);
            setNotification({
              show: true,
              type: "success",
              message: `Arrival notification ${action} successfully. You can now send it from the summary page.`,
            });
          }}
        />

        {/* Notification */}
        <Notifier
          show={notification.show}
          type={notification.type}
          message={notification.message}
          onClose={() =>
            setNotification({ show: false, type: "info", message: "" })
          }
        />
      </div>
    </AppLayout>
  );
}
